import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { ClientBFFContext } from "@finbackoffice/site-core";

export type ICardWithdrawForm = {
    amount: string;
    card_holder: string;
    card_number: string;
    card_expiration: string;
};
import { WalletsContext, MultiWalletDataContext } from "contexts";

export const useCardWithdrawFormMulti = () => {
    const { currentCurrency } = useContext(WalletsContext);
    const { setStep, setWithdrawResponse, selectedProvider, selectedPaymentMethod } =
        useContext(MultiWalletDataContext);
    const client = useContext(ClientBFFContext);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const formMethods = useForm<ICardWithdrawForm>({
        mode: "onChange",
        defaultValues: { amount: "", card_holder: "", card_number: "", card_expiration: "" },
    });
    const formValues = useWatch({ control: formMethods.control });

    useEffect(() => {
        setError("");
    }, [formValues]);

    const clickWithdraw = useCallback(
        async (data: ICardWithdrawForm) => {
            if (!selectedProvider || !currentCurrency || !selectedPaymentMethod) return;
            try {
                setLoading(true);
                const response = await client.createWithdrawal({
                    provider: selectedProvider,
                    currency: currentCurrency,
                    amount: data.amount,
                    provider_data: {
                        payment_method: selectedPaymentMethod,
                        card_number: data.card_number.replaceAll(" ", ""),
                        card_holder: data.card_holder,
                        card_expiration: data.card_expiration.replaceAll(" ", ""),
                    },
                });

                if (response) {
                    setWithdrawResponse(response);
                    setStep("status");
                }
            } catch (err: any) {
                const error = err.response.data ?? {};

                if (error.error) {
                    const details = error.details;

                    switch (error.error) {
                        case "payments_fulfillment_exception": {
                            let detailedMessage = "";

                            if (details) {
                                if (details.max_withdrawal_error) {
                                    detailedMessage = details.max_withdrawal_error;
                                } else {
                                    for (const key in details) {
                                        detailedMessage += details[key] + "\n";
                                    }
                                }
                            }

                            if (detailedMessage) {
                                setError(detailedMessage);
                            } else {
                                setError(error.message);
                            }
                            break;
                        }
                        case "validation_error": {
                            if (details) {
                                if (details.provider_data) {
                                    for (const field in details.provider_data) {
                                        let detailedMessage = "";
                                        for (const key in details.provider_data[field]) {
                                            detailedMessage +=
                                                details.provider_data[field][key] + "\n";
                                        }
                                        formMethods.setError(field as keyof ICardWithdrawForm, {
                                            message: detailedMessage,
                                        });
                                    }
                                }
                            } else {
                                setError(error.message);
                            }
                            break;
                        }
                        default:
                            setError(error.message);
                    }
                }

                setLoading(false);
            }
        },
        [
            selectedProvider,
            currentCurrency,
            selectedPaymentMethod,
            client,
            setWithdrawResponse,
            setStep,
            formMethods,
        ],
    );

    return useMemo(
        () => ({
            clickWithdraw,
            error,
            loading,
            formMethods,
        }),
        [clickWithdraw, error, formMethods, loading],
    );
};
